// ============================================================================
// src/layer/LayerEntry.tsx — the layer domain's nested routes.
// ============================================================================
// B3.1 finish, third domain. Replaces resolveLayerEntry. See
// src/cattle/CattleEntry.jsx for the pattern and
// src/app/navigation/viewRoutes.js for why `location` is the ROUTED location.
//
// layerbatches is mounted twice on purpose: it owns the housings record space
// as well as its own, and two <Route> elements pointing at one component is
// how a route table says that. Both carry identical props — now none at all.
// ============================================================================
import React from 'react';

import {AppRoute, AppRoutes} from '../app/navigation/index.js';
import {LAYER_ROUTES} from './layerRoutes.js';

import LayersHomeView from './LayersHomeView.tsx';
import LayersView from './LayersView.tsx';
import LayerBatchesView from './LayerBatchesView.tsx';
import LayerDailysView from './LayerDailysView.tsx';
import EggDailysView from './EggDailysView.tsx';
import PageBoundary from '../app/PageBoundary.tsx';

export {LAYER_ROUTES};

// Declared here rather than shared with the other four domain entries: they
// have the same shape, not the same module, and a cross-domain import would add
// an edge to the graph the route guards walk.
type RoutedLocationProp = string | {pathname: string; search?: string; hash?: string; state?: unknown};

// `layerGroups` is the ppp-layer-groups-v1 app_store blob. Its element shape
// lives in SQL and in the persisted blob, NOT in any TypeScript declaration
// this repo owns, and this component only forwards it — so `unknown[]` is the
// honest element type. Anything more specific here would be a guarantee the
// Supabase client cannot make.
type RowList = unknown[];

// SIX PROPS LEFT THIS ENTRY AND DID NOT COME BACK.
//
// `layerBatches`, `layerHousings` and `batches` were App's former-eager boot
// results. Every layer surface that needed them now reads them itself —
// layer.groups.core for /layer/groups, layer.dailys.groupDirectory for both
// daily surfaces, layer.record.core + layer.record.appStore for the two record
// surfaces — which is what lets `layers`, `layerdailys` and `layerbatches`
// retire from the frozen consumer lists in
// tests/fixtures/former-eager-dependencies.json. `feedCosts` left for a
// different reason: LayerBatchPage declared it, destructured it, and never read
// it.
//
// `setLayerBatches` and `persistLayerHousings` outlived those by one wave,
// because a write is not a read. They left with the `layer.batches.detail` boot
// release: LayerBatchPage was the last consumer of either, and neither was ever
// a database write — that page's three writes are page-local and did not move.
// `persistLayerHousings` had no caller left at all afterwards and was deleted
// from App outright.
//
// `layerGroups` NO LONGER REACHES ANY ROUTE, and it is still declared here.
//
// /layer/eggs was its last consumer; the egg hub reads the ppp-layer-groups-v1
// blob itself through layer.eggDailys.groups now, which is what retires
// `eggdailys` from the frozen app_store consumer list. The DECLARATION stays
// because App still passes the value, and App is the integration hub this wave
// deliberately does not touch — so the prop is accepted and never destructured
// rather than removed here and left unmatched at the only call site. Deleting
// it is a two-line App change (`layerGroups,` out of the createElement literal,
// and this field out of the type) and belongs to whoever next opens App.tsx.
//
// `persistLayerGroups` is accepted only for App dispatch compatibility now.
// /layer/groups owns the ppp-layer-groups-v1 write through saveAppStoreBlob,
// and no route below receives this callback. Leaving the type field in place
// keeps this lane out of App.tsx while making a future removal of the obsolete
// dispatch argument a deliberate integration change rather than a silent cast.
type LayerEntryProps = {
  location: RoutedLocationProp;
  layerGroups: RowList;
};

export function LayerEntry({location}: LayerEntryProps) {
  // NO PROPS AT ALL. The record surfaces read the directories, the layer-groups
  // blob and the broiler batches through their own resources now, and both of
  // them were boot-released, which retired the two writer props this element
  // used to carry. Handing any array down here would restore the exact
  // consumption tests/static/former_eager_evidence_static.test.js derives and
  // make the `layerbatches` retirements false.
  const batchesElement = <LayerBatchesView />;
  const routes = (
    <AppRoutes location={location}>
      <AppRoute path="/layer" element={<LayersHomeView />} />
      {/* /layer/groups owns both sides: layer.groups.core reads the blob and
          directories, and LayersView writes the blob through the shared
          app-store transport seam. No App callback reaches this release. */}
      <AppRoute path="/layer/groups" element={<LayersView />} />
      <AppRoute path="/layer/batches/*" element={batchesElement} />
      <AppRoute path="/layer/housings/*" element={batchesElement} />
      {/* No props at all. Both layer daily surfaces read the group directory
          through layer.dailys.groupDirectory, mounted in LayerDailysRouter
          above the index/record branch — which is exactly where this hand-off
          used to land. */}
      <AppRoute path="/layer/dailys/*" element={<LayerDailysView />} />
      {/* No props at all. The egg hub reads the ppp-layer-groups-v1 blob
          through layer.eggDailys.groups; handing the array down here would
          restore the exact consumption
          tests/static/former_eager_evidence_static.test.js derives, and make
          the `eggdailys` retirement false. */}
      <AppRoute path="/layer/eggs/*" element={<EggDailysView />} />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default LayerEntry;
